import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { motion } from 'framer-motion'
import Meta from '../components/common/Meta'
import Sectionbreak from '../components/Sectionbreak'
import { CheckCircle } from 'lucide-react'
import { Navigate, useParams } from 'react-router-dom'
import { services } from '../constants/services'
import AnimatedSection from '../components/common/AnimatedSection'
import headerImage from '../assets/images/services/services-header.jpg'

function ServiceDetails() {
    const [selectedImage, setSelectedImage] = useState(null)
    const { slug } = useParams()
    const service = services.find((s) => s.slug === slug)

  if (!service) {
    return <Navigate to="/services" replace />
  }

  const {
    title,
    shortDescription,
    image,
    gallery,
    description,
    features,
    process,
  } = service

  return (
    <>
      {/* SEO */}
      <Meta
        title={`${title} | Edges Design Group`}
        description={shortDescription}
      />

    <header
        className="relative min-h-[320px] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${headerImage})` }}
        >

        {/* Content */}
        <div className="relative z-10 text-center p-4 rounded-lg max-w-4xl">
            <h1 className="text-3xl md:text-5xl font-bold text-white tracking-wide drop-shadow-lg">
            {title}
            </h1>

            <p className="mt-4 text-lg md:text-xl text-white drop-shadow-md">
            {shortDescription}
            </p>
        </div>
        </header>

          {/* GALLERY */}
        <AnimatedSection className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
            <Sectionbreak
            title="Service"
            highlight="Gallery"
            description="A visual showcase of our expertise and completed works."
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-10">
            {gallery?.map((img, index) => (
              <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="group relative overflow-hidden rounded-2xl shadow-md cursor-pointer"
                    onClick={() => setSelectedImage(index)}
                    >
                    <img
                        src={gallery[index]}
                        alt={`${title} project ${index + 1}`}
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center">
                        <span className="text-white text-sm tracking-wide uppercase">
                        View Image
                        </span>
                    </div>
                    </motion.div>


            ))}
            </div>
        </div>
        </AnimatedSection>

      {/* FEATURES */}
      <AnimatedSection className="py-16 bg-white">
          <div className="bg-gray-50 p-8 rounded-xl shadow-sm">
            <h3 className="text-xl text-center font-semibold mb-6">What We Offer</h3>

            <div className="grid grid-cols-4 sm:grid-cols-4 gap-5">
              {features.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center align-middle cursor-pointer gap-3 bg-white p-4 rounded-lg shadow-xs"
                >
                  <span className="w-2 h-2 rounded-full bg-teal-800"></span>
                  <span className="text-sm text-gray-700 leading-relaxed">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
      </AnimatedSection>


      {/* PROCESS */}
      <AnimatedSection className="py-16 bg-gradient-to-br from-[#fdf1eb] via-[#f1f9fa] to-[#e4f4fd]">
        <div className="max-w-7xl mx-auto px-6">
          <Sectionbreak
            title="Our"
            highlight="Process"
            description="A clear and structured workflow ensuring quality, efficiency, and transparency."
          />

          <div className="grid md:grid-cols-4 gap-6 mt-10">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm"
              >
                <span className="text-teal-600 font-bold text-lg">
                  {index + 1}.
                </span>
                <h4 className="font-semibold mt-3 mb-2">{step.title}</h4>
                <p className="text-sm font-light text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection className="py-16 bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="mb-6 text-teal-100 text-lg font-light">
            Talk to our experts and bring your vision to life.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-teal-700 px-12 py-3 rounded-md font-semibold hover:bg-gray-100 transition"
          >
            Contact Us
          </a>
        </div>
      </AnimatedSection>


      {selectedImage !== null && (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center px-4"
  >
    {/* Close Button */}
    <button
      onClick={() => setSelectedImage(null)}
      className="absolute top-6 right-6 text-white hover:text-gray-300 transition"
    >
      <X size={32} />
    </button>

    {/* Left Arrow */}
    {selectedImage > 0 && (
      <button
        onClick={() => setSelectedImage(selectedImage - 1)}
        className="absolute left-6 text-white hover:text-gray-300 transition"
      >
        <ChevronLeft size={40} />
      </button>
    )}

    {/* Right Arrow */}
    {selectedImage < service.gallery.length - 1 && (
      <button
        onClick={() => setSelectedImage(selectedImage + 1)}
        className="absolute right-6 text-white hover:text-gray-300 transition"
      >
        <ChevronRight size={40} />
      </button>
    )}

    {/* Image */}
    <motion.img
      src={service.gallery[selectedImage]}
      alt="Full view"
      initial={{ scale: 0.9 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3 }}
      className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
    />
  </motion.div>
)}


    </>
  )

}

export default ServiceDetails
